/**
 * Work Order Queue
 *
 * Prioritized list of approved work orders for PM management.
 * Shows rush orders, status, due dates, and quick actions.
 *
 * Features:
 * - Prioritized queue (rush first, then by due date)
 * - Status filtering (draft, submitted, in_production, completed)
 * - Quick status updates
 * - Work order detail expansion
 */

import { useState, useEffect, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

const apiScopes = [
  "api://2350508b-6d67-408a-9c55-c5040357d90d/b351e950-d526-4ee4-b95b-03f6ec89cd3d/access_as_user",
];

// Status colors and labels
const STATUS_CONFIG = {
  draft: { bg: "bg-slate-100", text: "text-gray-600", label: "Draft" },
  submitted: { bg: "bg-blue-100", text: "text-blue-600", label: "Submitted" },
  in_production: { bg: "bg-purple-100", text: "text-purple-600", label: "In Production" },
  on_hold: { bg: "bg-yellow-100", text: "text-yellow-600", label: "On Hold" },
  completed: { bg: "bg-green-100", text: "text-green-600", label: "Completed" },
  cancelled: { bg: "bg-red-100", text: "text-red-600", label: "Cancelled" },
};

// Status badge component
function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  return (
    <span className={`text-xs px-2 py-1 rounded ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
}

// Work order card component
function WorkOrderCard({ workOrder, onSelect, onStatusChange, isSelected }) {
  const {
    id,
    wo_number,
    project_name,
    customer_name,
    quote_number,
    due_date,
    ship_date,
    status,
    is_rush,
    is_on_hold,
    assigned_pm_name,
    item_count,
    created_at,
  } = workOrder;

  // Format dates
  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  // Calculate urgency
  const getDaysUntil = (dateStr) => {
    if (!dateStr) return null;
    return Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
  };

  const daysUntilDue = getDaysUntil(due_date);
  const isOverdue = daysUntilDue !== null && daysUntilDue < 0;
  const isDueSoon = daysUntilDue !== null && daysUntilDue <= 3 && daysUntilDue >= 0;

  return (
    <div
      className={`
        border rounded-lg p-4 cursor-pointer transition-all
        ${isSelected ? "border-blue-500 bg-blue-50/50 ring-2 ring-blue-200" : "border-gray-200 hover:border-gray-300 hover:shadow-sm"}
        ${is_rush ? "border-l-4 border-l-red-500" : ""}
        ${isOverdue && !is_rush ? "border-l-4 border-l-orange-500" : ""}
      `}
      onClick={() => onSelect(workOrder)}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-sm text-gray-500">{wo_number}</span>
            {is_rush && (
              <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-700 rounded font-medium">
                RUSH
              </span>
            )}
            {is_on_hold && (
              <span className="text-xs px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded">
                HOLD
              </span>
            )}
          </div>
          <h3 className="font-medium text-gray-900 truncate" title={project_name}>
            {project_name || "Untitled Project"}
          </h3>
          <p className="text-sm text-gray-500 truncate">
            {customer_name} • Q{quote_number}
          </p>
        </div>
        <StatusBadge status={status} />
      </div>

      {/* Details row */}
      <div className="flex items-center gap-4 text-sm text-gray-500 mt-3">
        <div className="flex items-center gap-1">
          <span className="text-gray-500">Due:</span>
          <span className={`font-medium ${isOverdue ? "text-red-600" : isDueSoon ? "text-orange-600" : "text-gray-700"}`}>
            {formatDate(due_date)}
            {daysUntilDue !== null && (
              <span className="ml-1 text-xs">
                ({isOverdue ? `${Math.abs(daysUntilDue)}d late` : `${daysUntilDue}d`})
              </span>
            )}
          </span>
        </div>
        {ship_date && (
          <div className="flex items-center gap-1">
            <span className="text-gray-500">Ship:</span>
            <span className="font-medium text-gray-700">{formatDate(ship_date)}</span>
          </div>
        )}
        {item_count && (
          <div className="flex items-center gap-1">
            <span className="text-gray-500">Items:</span>
            <span className="font-medium text-gray-700">{item_count}</span>
          </div>
        )}
      </div>

      {/* PM Assignment */}
      {assigned_pm_name && (
        <div className="mt-2 text-xs text-gray-500">
          PM: {assigned_pm_name}
        </div>
      )}
    </div>
  );
}

// Work order detail panel
function WorkOrderDetail({ workOrder, items, events, onStatusChange, onClose }) {
  if (!workOrder) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-500">
        <div className="text-center">
          <div className="text-4xl mb-2">📋</div>
          <p>Select a work order to view details</p>
        </div>
      </div>
    );
  }

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b px-6 py-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono text-lg text-gray-600">{workOrder.wo_number}</span>
              {workOrder.is_rush && (
                <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-sm font-medium">
                  RUSH
                </span>
              )}
              <StatusBadge status={workOrder.status} />
            </div>
            <h2 className="text-xl font-bold text-gray-900">
              {workOrder.project_name || "Untitled Project"}
            </h2>
            <p className="text-gray-500">
              {workOrder.customer_name} • Quote {workOrder.quote_number}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg text-gray-500 hover:text-gray-600 lg:hidden"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        {/* Key Dates */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-slate-200 rounded-lg p-4">
            <div className="text-sm text-gray-500 mb-1">Due Date</div>
            <div className="font-semibold text-gray-900">
              {workOrder.due_date ? new Date(workOrder.due_date).toLocaleDateString() : "Not set"}
            </div>
          </div>
          <div className="bg-slate-200 rounded-lg p-4">
            <div className="text-sm text-gray-500 mb-1">Ship Date</div>
            <div className="font-semibold text-gray-900">
              {workOrder.ship_date ? new Date(workOrder.ship_date).toLocaleDateString() : "Not set"}
            </div>
          </div>
        </div>

        {/* Description */}
        {workOrder.description && (
          <div>
            <h3 className="font-medium text-gray-700 mb-2">Description</h3>
            <p className="text-gray-600 whitespace-pre-wrap">{workOrder.description}</p>
          </div>
        )}

        {/* Special Instructions */}
        {workOrder.special_instructions && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h3 className="font-medium text-yellow-800 mb-2">Special Instructions</h3>
            <p className="text-yellow-700 whitespace-pre-wrap">{workOrder.special_instructions}</p>
          </div>
        )}

        {/* Line Items */}
        {items && items.length > 0 && (
          <div>
            <h3 className="font-medium text-gray-700 mb-3">Line Items ({items.length})</h3>
            <div className="space-y-2">
              {items.map((item, idx) => (
                <div key={item.id || idx} className="bg-white border rounded-lg p-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-medium text-gray-900">{item.item_name || item.sku}</div>
                      {item.description && (
                        <p className="text-sm text-gray-500 mt-1">{item.description}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="font-medium">Qty: {item.quantity || 1}</div>
                      {item.status && <StatusBadge status={item.status} />}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Event History */}
        {events && events.length > 0 && (
          <div>
            <h3 className="font-medium text-gray-700 mb-3">Activity</h3>
            <div className="space-y-2">
              {events.slice(0, 10).map((event, idx) => (
                <div key={event.id || idx} className="flex items-start gap-3 text-sm">
                  <div className="w-2 h-2 mt-1.5 rounded-full bg-gray-300" />
                  <div className="flex-1">
                    <span className="text-gray-600">{event.event_type?.replace(/_/g, " ")}</span>
                    {event.actor_name && (
                      <span className="text-gray-500"> by {event.actor_name}</span>
                    )}
                    <div className="text-xs text-gray-500 mt-0.5">
                      {formatDateTime(event.occurred_at)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="border-t pt-4">
          <h3 className="font-medium text-gray-700 mb-3">Quick Actions</h3>
          <div className="flex flex-wrap gap-2">
            {workOrder.status === "draft" && (
              <button
                onClick={() => onStatusChange(workOrder.id, "submitted")}
                className="px-4 py-2 bg-blue-600 text-gray-900 rounded-lg hover:bg-blue-700"
              >
                Submit for Production
              </button>
            )}
            {workOrder.status === "submitted" && (
              <button
                onClick={() => onStatusChange(workOrder.id, "in_production")}
                className="px-4 py-2 bg-purple-600 text-gray-900 rounded-lg hover:bg-purple-700"
              >
                Start Production
              </button>
            )}
            {workOrder.status === "in_production" && (
              <button
                onClick={() => onStatusChange(workOrder.id, "completed")}
                className="px-4 py-2 bg-green-600 text-gray-900 rounded-lg hover:bg-green-700"
              >
                Mark Complete
              </button>
            )}
            {!workOrder.is_on_hold && workOrder.status !== "completed" && (
              <button
                onClick={() => onStatusChange(workOrder.id, "on_hold")}
                className="px-4 py-2 border border-yellow-500 text-yellow-700 rounded-lg hover:bg-yellow-50"
              >
                Put On Hold
              </button>
            )}
            {workOrder.is_on_hold && (
              <button
                onClick={() => onStatusChange(workOrder.id, workOrder.status === "on_hold" ? "in_production" : workOrder.status)}
                className="px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg hover:bg-yellow-600"
              >
                Resume
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function WorkOrderQueue() {
  const { instance, accounts } = useMsal();
  const [workOrders, setWorkOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null); // { workOrder, items, events }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("active"); // "all" | "active" | "draft" | "completed"

  // Acquire access token
  const acquireAccessToken = useCallback(async () => {
    try {
      const response = await instance.acquireTokenSilent({
        scopes: apiScopes,
        account: accounts[0],
      });
      return response.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        const response = await instance.acquireTokenPopup({ scopes: apiScopes });
        return response.accessToken;
      }
      throw err;
    }
  }, [instance, accounts]);

  // Load work orders
  const loadWorkOrders = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const accessToken = await acquireAccessToken();
      if (!accessToken) {
        setError("Authentication required");
        return;
      }

      // Build query params based on filter
      let queryParams = "";
      if (statusFilter === "active") {
        queryParams = "?status=in_production";
      } else if (statusFilter === "draft") {
        queryParams = "?status=draft";
      } else if (statusFilter === "completed") {
        queryParams = "?status=completed";
      }

      const res = await fetch(`/api/pm-center/work-orders/queue${queryParams}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!res.ok) {
        throw new Error("Failed to load work orders");
      }

      const data = await res.json();
      setWorkOrders(data.workOrders || []);
    } catch (err) {
      console.error("Error loading work orders:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [acquireAccessToken, statusFilter]);

  // Load work order details
  const loadWorkOrderDetail = useCallback(async (workOrderId) => {
    try {
      const accessToken = await acquireAccessToken();
      if (!accessToken) return;

      const res = await fetch(`/api/pm-center/work-orders/${workOrderId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!res.ok) {
        throw new Error("Failed to load work order details");
      }

      const data = await res.json();
      setSelectedDetail({
        workOrder: data.workOrder,
        items: data.items || [],
        events: data.events || [],
      });
    } catch (err) {
      console.error("Error loading work order details:", err);
    }
  }, [acquireAccessToken]);

  // Update work order status
  const handleStatusChange = useCallback(async (workOrderId, newStatus) => {
    try {
      const accessToken = await acquireAccessToken();
      if (!accessToken) return;

      const res = await fetch(`/api/pm-center/work-orders/${workOrderId}/status`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!res.ok) {
        throw new Error("Failed to update status");
      }

      // Reload work orders and details
      await loadWorkOrders();
      if (selectedOrder?.id === workOrderId) {
        await loadWorkOrderDetail(workOrderId);
      }
    } catch (err) {
      console.error("Error updating status:", err);
      setError(err.message);
    }
  }, [acquireAccessToken, loadWorkOrders, loadWorkOrderDetail, selectedOrder]);

  // Handle work order selection
  const handleSelectOrder = useCallback((workOrder) => {
    setSelectedOrder(workOrder);
    loadWorkOrderDetail(workOrder.id);
  }, [loadWorkOrderDetail]);

  // Initial load
  useEffect(() => {
    loadWorkOrders();
  }, [loadWorkOrders]);

  // Poll for updates every 60 seconds
  useEffect(() => {
    const interval = setInterval(loadWorkOrders, 60000);
    return () => clearInterval(interval);
  }, [loadWorkOrders]);

  // Filter counts for tabs
  const filterCounts = {
    all: workOrders.length,
    active: workOrders.filter((wo) => wo.status === "in_production").length,
    draft: workOrders.filter((wo) => wo.status === "draft").length,
    completed: workOrders.filter((wo) => wo.status === "completed").length,
  };

  return (
    <div className="h-full flex flex-col bg-slate-200">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Work Order Queue</h1>
            <p className="text-sm text-gray-500">
              {workOrders.length} work orders
            </p>
          </div>
          <button
            onClick={loadWorkOrders}
            disabled={loading}
            className="px-3 py-1.5 border rounded-lg text-sm hover:bg-slate-200 disabled:opacity-50"
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        {/* Status filter tabs */}
        <div className="flex gap-2">
          {[
            { key: "active", label: "In Production" },
            { key: "draft", label: "Drafts" },
            { key: "completed", label: "Completed" },
            { key: "all", label: "All" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setStatusFilter(tab.key)}
              className={`
                px-3 py-1.5 rounded-lg text-sm font-medium transition-colors
                ${statusFilter === tab.key
                  ? "bg-white text-gray-900"
                  : "bg-slate-100 text-gray-600 hover:bg-gray-200"
                }
              `}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Work order list */}
        <div className="w-full lg:w-1/2 xl:w-2/5 border-r overflow-y-auto p-4">
          {loading && workOrders.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-gray-500">
              Loading work orders...
            </div>
          ) : workOrders.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-gray-500">
              <div className="text-3xl mb-2">📋</div>
              <p>No work orders found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {workOrders.map((wo) => (
                <WorkOrderCard
                  key={wo.id}
                  workOrder={wo}
                  onSelect={handleSelectOrder}
                  onStatusChange={handleStatusChange}
                  isSelected={selectedOrder?.id === wo.id}
                />
              ))}
            </div>
          )}
        </div>

        {/* Detail panel */}
        <div className="hidden lg:flex lg:flex-1 bg-white">
          <WorkOrderDetail
            workOrder={selectedDetail?.workOrder}
            items={selectedDetail?.items}
            events={selectedDetail?.events}
            onStatusChange={handleStatusChange}
            onClose={() => {
              setSelectedOrder(null);
              setSelectedDetail(null);
            }}
          />
        </div>
      </div>
    </div>
  );
}
